
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Clock, Info, AlertCircle } from 'lucide-react';
import { TeacherAvailabilityManager } from '@/components/teacher/availability/TeacherAvailabilityManager';
import { useTeacherAvailability } from '@/hooks/useTeacherAvailability';

interface AvailabilityVerificationStepProps {
  teacherCode: string;
  onComplete: (data: any) => Promise<boolean>;
  onBack: () => void;
}

export function AvailabilityVerificationStep({ teacherCode, onComplete, onBack }: AvailabilityVerificationStepProps) {
  const [stepConfirmed, setStepConfirmed] = useState(false);
  const [notAvailableForMore, setNotAvailableForMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { weeklySchedule } = useTeacherAvailability(teacherCode);
  const hasAtLeastOneDay = Object.values(weeklySchedule).some(
    (slots) => Array.isArray(slots) && slots.length > 0
  );
  const availabilityRequirementMet = hasAtLeastOneDay || notAvailableForMore;
  const canSubmit = stepConfirmed && availabilityRequirementMet;

  const handleComplete = async () => {
    if (stepConfirmed) {
      setIsSubmitting(true);
      
      await onComplete({
        step1Confirmed: true,
        step2Confirmed: true,
        step3Confirmed: true,
        verifiedAt: new Date().toISOString()
      });

      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <Badge variant="outline" className="mb-4">
          ขั้นตอนที่ 3: ยืนยันช่วงเวลารับงานสอน
        </Badge>
      </div>

      {/* Encouraging Message */}
      <Alert className="bg-blue-50 border-blue-200">
        <Clock className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          <div className="space-y-2">
            <p className="font-medium">⏰ เชิญชวนให้ครูกรอกช่วงเวลารับงานสอนให้ครบถ้วน</p>
            <p className="text-sm">
              การกรอกช่วงเวลารับงานสอนให้ถูกต้องและครบถ้วน 
              จะช่วยอำนวยความสะดวกในการจับคู่ระหว่างครูกับนักเรียน
            </p>
            <p className="text-sm font-medium text-blue-700">
              🎯 ยิ่งช่วงเวลาครบถ้วน ยิ่งได้โอกาสสอนมากขึ้น!
            </p>
          </div>
        </AlertDescription>
      </Alert>

      {/* Explanation */}
      <Alert className="bg-amber-50 border-amber-200">
        <Info className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <div className="space-y-2">
            <p className="font-medium">📋 คำอธิบายเกี่ยวกับช่วงเวลารับงานสอน</p>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>ช่วงเวลารับงานสอน ไม่ใช่ตารางสอน แต่เป็นเวลาทั้งหมดที่คุณครูสะดวกที่จะรับงานสอนในแต่ละสัปดาห์</li>
              <li>หากครูมีสอนกับนักเรียนคนใดอยู่แล้ว ช่วงเวลานั้นก็ถือว่าเป็นช่วงเวลาที่รับสอน</li>
              <li>สถาบันจะอัพเดทตารางสอนของครูจาก ตารางประจำ เข้ามาในภายหลัง</li>
            </ul>
          </div>
        </AlertDescription>
      </Alert>

      {/* การจัดการช่วงเวลารับงานสอน */}
      <div>
        <h4 className="text-md font-semibold text-amber-800 mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5" />
          การจัดการช่วงเวลารับงานสอน
        </h4>
        <TeacherAvailabilityManager teacherCode={teacherCode} />

        {!availabilityRequirementMet && (
          <Alert className="mt-4 bg-red-50 border-red-200">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              กรุณาเพิ่มช่วงเวลารับงานสอนอย่างน้อย 1 วัน หรือเลือก "ไม่สะดวกรับงานสอนเพิ่ม" ด้านล่าง
            </AlertDescription>
          </Alert>
        )}

        <div className="flex items-center space-x-2 p-4 mt-4 bg-muted/40 border rounded-lg">
          <Checkbox
            id="notAvailableForMore"
            checked={notAvailableForMore}
            onCheckedChange={(checked) => setNotAvailableForMore(checked as boolean)}
          />
          <Label htmlFor="notAvailableForMore" className="text-sm font-medium">
            ฉันไม่สะดวกรับงานสอนเพิ่มในช่วงนี้ (ไม่ต้องกรอกช่วงเวลา)
          </Label>
        </div>
      </div>

      {/* Confirmation Checkbox */}
      <div className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg">
        <Checkbox
          id="step3Confirmed"
          checked={stepConfirmed}
          onCheckedChange={(checked) => setStepConfirmed(checked as boolean)}
        />
        <Label htmlFor="step3Confirmed" className="text-sm font-medium text-green-800">
          ข้าพเจ้าได้ตรวจสอบข้อมูลช่วงเวลารับงานสอนข้างต้นเรียบร้อยแล้ว และยืนยันว่าข้อมูลถูกต้องครบถ้วน *
        </Label>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={isSubmitting}
        >
          กลับขั้นตอนก่อนหน้า
        </Button>
        <Button
          onClick={handleComplete}
          disabled={!canSubmit || isSubmitting}
          className="bg-green-600 hover:bg-green-700"
        >
          {isSubmitting ? 'กำลังยืนยัน...' : 'ยืนยันข้อมูล'}
        </Button>
      </div>
    </div>
  );
}
